import React from 'react';
import { observer } from 'mobx-react-lite';
import {
	InputGroup,
	Img,
	Panel,
	Typography,
	Button,
	Div,
} from '@icecreamsocial/components';
import { useFormik } from 'formik';
import {
	RequiredAsterisk,
	EnhancedForm2,
	ImageUploader,
} from '../../../../presentational';
import { flags } from '../../../../../utils';

const ShareSettings = ({
	shareTitle = undefined,
	shareMessage = undefined,
	shareImage = undefined,
	emailSubject = undefined,
	emailNote = '',
	redirectUrl = '',
	discountCode = '',
	discountCodeDesc = '',
	allowRouteBlock = true,
	renderControls = null,
	onSubmit,
}) => {
	/**
	 * Recommended/example text
	 */
	const recommendedShareTitle = 'Use code INVITE10 to get 10% off!';
	const recommendedShareMessage =
		'I just bought {{ product.name }} and you should too! Use code INVITE10 to get 10% off!';
	const recommendedEmailSubject =
		'{{ influencer.name }} sent you a BUSINESS NAME 10% off Promo Code!';

	/**
	 * default text
	 */
	const defaultShareTitle = shareTitle ?? recommendedShareTitle;
	const defaultShareMessage = shareMessage ?? recommendedShareMessage;
	const defaultEmailSubject = emailSubject ?? recommendedEmailSubject;

	const formikProps = {
		enableReinitialize: true,

		initialValues: {
			shareTitle: defaultShareTitle,
			shareMessage: defaultShareMessage,
			emailSubject: defaultEmailSubject,
			discountCode,
			discountCodeDesc,
			shareImage,
			emailNote,
			redirectUrl,
		},
		validate: (values) => {
			const ONE_MB = 1024 * 1024; // 1 MB
			const errors = {};
			if (!values.shareImage) {
				errors.shareImage = 'Please upload a Share Image';
			}

			if (values.shareImage?.size > ONE_MB) {
				errors.shareImage = `Your image has exceeded the maximum file size of 1MB. (Size: ${(
					values.shareImage?.size / ONE_MB || 0
				).toFixed(2)}MB )`;
			}

			if (!values.redirectUrl?.match(/https?:\/\//)) {
				errors.redirectUrl = 'Enter a full URL including http:// or https://';
			}

			return errors;
		},
		onSubmit,
	};

	return (
		<EnhancedForm2
			formikProps={formikProps}
			promptIf={({ dirty }) => allowRouteBlock && dirty}
			renderControls={renderControls}
		>
			{(shareSettings) => (
				<>
					<Panel backgroundColor='bodyInverse' marginBottom='lg'>
						<Panel.Header
							backgroundColor='bodyInverse'
							textColor='body'
							paddingBottom='0'
						>
							<Div grid gridColumns='repeat(3, 1fr)' alignItems='center'>
								<Typography.H2
									textColor='body'
									textAlign='center'
									margin='none'
									typoSize='md'
									gridColumn='2'
								>
									Share Copy
								</Typography.H2>
								<Button
									type='button'
									textDecoration={
										shareSettings.values.shareMessage !==
											recommendedShareMessage ||
										shareSettings.values.shareTitle !== recommendedShareTitle
											? 'underline'
											: undefined
									}
									textColor={
										shareSettings.values.shareMessage !==
											recommendedShareMessage ||
										shareSettings.values.shareTitle !== recommendedShareTitle
											? 'link'
											: 'ambient'
									}
									onClick={() => {
										shareSettings.setFieldValue(
											'shareTitle',
											recommendedShareTitle
										);
										shareSettings.setFieldValue(
											'shareMessage',
											recommendedShareMessage
										);
									}}
									backgroundColor='transparent'
									shadow='none'
									hoverShadow='none'
									border='none'
									padding='none'
								>
									Use Recommended Text
								</Button>
							</Div>

							<Typography.P marginBottom='0'>
								If an influencer chooses to share a campaign through social
								media platforms, this is the content that will appear in the
								share message. Any{' '}
								<Typography.Span
									as='code'
									backgroundColor='ambientLight'
									padding='2px'
									borderRadius='2px'
								>
									&#123;&#123; bracketed &#125;&#125;
								</Typography.Span>{' '}
								values are dynamic variables, and will be filled with the
								appropiate content when displayed.
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<InputGroup marginBottom='sm' elWidth='100%'>
								<InputGroup.Label>
									Share Title <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										id='shareTitle'
										name='shareTitle'
										paddingLeft='md'
										value={shareSettings.values.shareTitle}
										onChange={shareSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup>
							<InputGroup elWidth='100%'>
								<InputGroup.Label>
									Share Message <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										as='textarea'
										id='shareMessage'
										name='shareMessage'
										paddingLeft='md'
										rows='5'
										value={shareSettings.values.shareMessage}
										onChange={shareSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup>
						</Panel.Body>
					</Panel>

					<Panel backgroundColor='bodyInverse' marginBottom='lg'>
						<Panel.Header
							backgroundColor='bodyInverse'
							textColor='body'
							paddingBottom='0'
						>
							<Typography.H2
								textColor='body'
								textAlign='center'
								margin='none'
								typoSize='md'
							>
								Share Image <RequiredAsterisk />
							</Typography.H2>
							<Typography.P marginBottom='0'>
								This image will appear along with the Share Copy in shares and
								email invites
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<ImageUploader
								name='shareImage'
								value={shareSettings.values.shareImage}
								handleChange={(e) =>
									shareSettings.setFieldValue(
										'shareImage',
										e.currentTarget.files[0]
									)
								}
								onReplace={(e) => {
									e.preventDefault();
									shareSettings.setFieldValue('shareImage', undefined);
								}}
							/>
							{shareSettings.errors.shareImage && (
								<Typography.P textColor='bad'>
									{shareSettings.errors.shareImage}
								</Typography.P>
							)}
							{/* <InputGroup marginBottom="sm" elWidth="100%">
								<InputGroup.Label>
									Image URL <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight="md">
									<InputGroup.Input
										id="shareImage"
										name="shareImage"
										paddingLeft="md"
										value={shareSettings.values.shareImage}
										onChange={shareSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup> */}
							{/* {!!shareSettings.values.shareImage && (
								<Img
									src={shareSettings.values.shareImage}
									border="1px solid"
									borderColor="ambientDark"
									borderRadius="default"
									elMaxWidth="500px"
								/>
							)} */}
						</Panel.Body>
					</Panel>
					<Panel backgroundColor='bodyInverse' marginBottom='lg'>
						<Panel.Header
							backgroundColor='bodyInverse'
							textColor='body'
							paddingBottom='0'
						>
							<Div grid gridColumns='repeat(3, 1fr)' alignItems='center'>
								<Typography.H2
									textColor='body'
									textAlign='center'
									margin='none'
									typoSize='md'
									gridColumn='2'
								>
									Invite Email Content
								</Typography.H2>
								<Button
									type='button'
									textDecoration={
										shareSettings.values.emailSubject !==
										recommendedEmailSubject
											? 'underline'
											: undefined
									}
									textColor={
										shareSettings.values.emailSubject !==
										recommendedEmailSubject
											? 'link'
											: 'ambient'
									}
									onClick={() => {
										shareSettings.setFieldValue(
											'emailSubject',
											recommendedEmailSubject
										);
									}}
									backgroundColor='transparent'
									shadow='none'
									hoverShadow='none'
									border='none'
									padding='none'
								>
									Use Recommended Text
								</Button>
							</Div>

							<Typography.P marginBottom='0'>
								If an influencer chooses to share a campaign through email with
								their friends, this is the copy that will appear in that email.
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<InputGroup marginBottom='sm' elWidth='100%'>
								<InputGroup.Label>
									Email Subject <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										id='emailSubject'
										name='emailSubject'
										paddingLeft='md'
										value={shareSettings.values.emailSubject}
										onChange={shareSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup>
							<InputGroup elWidth='100%'>
								<InputGroup.Label>Additional Message</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										as='textarea'
										id='emailNote'
										name='emailNote'
										paddingLeft='md'
										rows='5'
										value={shareSettings.values.emailNote}
										onChange={shareSettings.handleChange}
									/>
								</InputGroup.Fields>
							</InputGroup>
						</Panel.Body>
					</Panel>
					<Panel backgroundColor='bodyInverse' marginBottom='lg'>
						<Panel.Header
							backgroundColor='bodyInverse'
							textColor='body'
							paddingBottom='0'
						>
							<Typography.H2
								textColor='body'
								textAlign='center'
								margin='none'
								typoSize='md'
							>
								Redirect URL
							</Typography.H2>
							<Typography.P marginBottom='0'>
								Clicking the share will take the invitee here
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<InputGroup marginBottom='sm' elWidth='100%'>
								<InputGroup.Label>
									Redirect URL <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										id='redirectUrl'
										name='redirectUrl'
										type='url'
										pattern='https?://.*'
										title='Enter a full URL including http:// or https://'
										paddingLeft='md'
										value={shareSettings.values.redirectUrl}
										onChange={shareSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
								{shareSettings.errors.redirectUrl && (
									<Typography.P textColor='bad'>
										{shareSettings.errors.redirectUrl}
									</Typography.P>
								)}
							</InputGroup>
						</Panel.Body>
					</Panel>
					<Panel backgroundColor='bodyInverse' marginBottom='lg'>
						<Panel.Header
							backgroundColor='bodyInverse'
							textColor='body'
							paddingBottom='0'
						>
							<Typography.H2
								textColor='body'
								textAlign='center'
								margin='none'
								typoSize='md'
							>
								Purchase Incentive
							</Typography.H2>
							<Typography.P marginBottom='0'>
								To get an extra boost in conversions, adding a purchase
								incentive can entice more customers to participate by granting
								them a discount or promotional code you've set up on our
								eCommerce platform. Turning this feature on will prompt invitees
								or prospect landing on an ICS installed page on your site to
								opt-in and enter their information in exchange for a discount.
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<InputGroup marginBottom='sm' elWidth='100%'>
								<InputGroup.Label>Discount Code</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										id='discountCode'
										name='discountCode'
										type='text'
										paddingLeft='md'
										value={shareSettings.values.discountCode}
										onChange={shareSettings.handleChange}
									/>
								</InputGroup.Fields>
							</InputGroup>
							<InputGroup marginBottom='sm' elWidth='100%'>
								<InputGroup.Label>Discount Code Description</InputGroup.Label>
								<InputGroup.Fields marginRight='md'>
									<InputGroup.Input
										as='textarea'
										id='discountCodeDesc'
										name='discountCodeDesc'
										value={shareSettings.values.discountCodeDesc}
										onChange={shareSettings.handleChange}
										paddingLeft='md'
										rows='5'
									/>
								</InputGroup.Fields>
							</InputGroup>
						</Panel.Body>
					</Panel>
				</>
			)}
		</EnhancedForm2>
	);
};
ShareSettings.displayName = 'ShareSettingsForm';

export default observer(ShareSettings);
